import { Container } from 'pixi.js'
import gsap from 'gsap'
import { ga4Info, sleep, soundManager, spineManager, useIsSpinning, useTurboStore } from '@slot-nx/core'
import { resetInitData, useGameStore } from '../../game/store/GameStore'
import { changeNode, createSymbolContainer, translatePotSkine } from '../../game/util/gameUtil'
import { GiftedSymbol } from '../../game/model'
import { SpineGroupType } from '../../game/enum/SpineGroupType'
import { SymbolType } from '../../game/enum/SymbolType'
import { tableService } from '../../table/tableService'
import { gameConfig } from '../../../config'
import { GameSoundType } from '../../sound/GameSoundType'
import { Bone, TrackEntry } from '@esotericsoftware/spine-pixi'
import { spinService } from '../../game'

interface ISpinService { spinOut(): Promise<void>, spinIn(): Promise<void>, shouldImmediatelyStop: boolean }

export class SpinService implements ISpinService {
  public reelNodes: Container[]
  private _reelsContainer: Container
  public spinId = 0
  public shouldImmediatelyStop = false
  private isErrorStop: boolean

  private initSpin(isTurbo: boolean) {
    useIsSpinning.setState(true)
    this.shouldImmediatelyStop = isTurbo
    this.isErrorStop = false
  }

  public setReelsContainer(container: Container) {
    this._reelsContainer = container
  }

  public async initReels(reels: Container[]) {
    this.reelNodes = reels
  }

  public async errorStop() {
    this.shouldImmediatelyStop = true
    resetInitData()
    await this.spinIn()
    useIsSpinning.setState(false)
  }

  public async spinOut() {
    const isTurbo = useTurboStore.getState().isTurbo
    useIsSpinning.setState(true)
    this.spinId++
    this.initSpin(isTurbo)
    return await this._rowByRowSpinOut()
  }

  public async spinIn() {
    const zeusGiftList = useGameStore.getState().getDropItem().giftedSymbols
    await this._playGiftSymbol(zeusGiftList)
    await this._rowByRowSpinIn(zeusGiftList)
    useIsSpinning.setState(false)
  }

  private async _rowByRowSpinOut(): Promise<void> {
    if (!this.reelNodes) return
    const isTurbo = useTurboStore.getState().isTurbo
    const dropHeight = this._reelsContainer.height
    const duration = 0.22
    const reelCount = this.reelNodes.length
    const animationPromises: Promise<void>[] = []
    const columnDelay = 0
    for (let i = 0; i < reelCount; i++) {
      const delay = isTurbo || this.shouldImmediatelyStop ? 0 : i * columnDelay
      const reel = this.reelNodes[i]
      const p = new Promise<void>((resolve) => {
        gsap.to(reel, {
          y: dropHeight,
          duration: duration,
          ease: 'power1.in',
          delay: delay,
          onComplete: resolve,
        })
      })
      animationPromises.push(p)
    }
    soundManager.play(GameSoundType.ThreeComboOlympus_SymbolOut)
    await Promise.all(animationPromises)
    this._reelsContainer.visible = false
    this.reelNodes.forEach(reel => reel.destroyChildren())
  }

  private async _rowByRowSpinIn(giftList: GiftedSymbol[]): Promise<void> {
    const dropTable = useGameStore.getState().getDropItem().finalTable
    const potTasks: Array<() => Promise<void>> = []
    const skipSet = new Set(giftList.map(g => `${g.position.x}_${g.position.y}`))
    const hiddenSymbols: Container[] = [] // ⭐ 存下要暫時隱藏的 symbol
    // 生成 symbols

    dropTable.forEach((columnData, columnIndex) => {
      columnData.forEach((rowData, rowIndex) => {
        const symbol = createSymbolContainer(rowData)
        symbol.y = rowIndex * gameConfig.nodeHeight - 580
        // symbol.y = rowIndex * gameConfig.nodeHeight
        const nodeName = `NodeId_${tableService.lowerId++}`
        symbol.name = nodeName
        this.reelNodes[columnIndex].addChild(symbol)
        const upperNode2 = tableService.generateUpperReelsNodeV2(nodeName, columnIndex, rowIndex) as Container
        if (skipSet.has(`${columnIndex}_${rowIndex}`)) {
          symbol.visible = false
          hiddenSymbols.push(symbol)
        }
        if ([66, 77, 88].includes(rowData.type)) {
          potTasks.push(() => spinService.playPotSymbolTips(rowData.type, upperNode2))
        }
      })
    })
    const isTurbo = useTurboStore.getState().isTurbo
    const dropHeight = this._reelsContainer.height
    const duration = 0.35
    const reelCount = this.reelNodes.length
    const animationPromises: Promise<void>[] = []
    const columnDelay = 0.08
    this.reelNodes.forEach(reel => gsap.set(reel, { y: -dropHeight }))

    this._reelsContainer.visible = true
    const isTurboOrShouldImmediatelyStop = isTurbo || this.shouldImmediatelyStop
    for (let i = 0; i < reelCount; i++) {
      const delay = isTurboOrShouldImmediatelyStop ? 0 : i * columnDelay
      const reel = this.reelNodes[i]
      const isFirstReel = i === 0
      reel.children.forEach((symbol, index) => {
        const symbolDelay = (reel.children.length - 1 - index) * 0.1
        const dropDuration = isTurboOrShouldImmediatelyStop ? 0 : 0.15

        const symbolPromise = new Promise<void>((resolveSymbol) => {
          const tl = gsap.timeline({ delay: symbolDelay, onComplete: resolveSymbol })
            .to(symbol, {
              y: '+=580',
              duration: dropDuration,
              ease: 'power2.out',
            })
          if (!isTurboOrShouldImmediatelyStop) {
            tl.to(symbol, { y: '+=12', duration: 0.05, ease: 'linear' })
              .to(symbol, { y: '-=12', duration: 0.09, ease: 'linear' })
          }
        })
        animationPromises.push(symbolPromise)
      })
      const p = new Promise<void>((resolve) => {
        const tl = gsap.timeline({ delay,
          onComplete: () => {
            (async () => {
              await sleep(300)
              // ⭐ 只在第一列做 pot tips
              if (isFirstReel) {
                if (potTasks.length) {
                  soundManager.play(GameSoundType.ThreeComboOlympus_SymbolTip)
                  await Promise.all(potTasks.map(task => task()))
                }
              }
              resolve()
            })()
          },
        })

        if (isTurboOrShouldImmediatelyStop) {
          tl.to(reel, { y: 10, duration: duration * 0.5, ease: 'power1.out' })
            .to(reel, { y: -10, duration: duration * 0.25, ease: 'power2.inOut' })
        }

        tl.to(reel, { y: 0, duration: duration * 0.5, ease: 'power1.out' })
      })
      animationPromises.push(p)
    }
    soundManager.play(GameSoundType.ThreeComboOlympus_SymbolEnter)
    await Promise.all(animationPromises)
    hiddenSymbols.forEach(s => s.visible = true)
    tableService.resetUpperReelsNode()
    useGameStore.setState({ isReadyToStop: false })
  }

  public async playPotSymbolTips(type: number, node: Container) {
    const spinPayout = useGameStore.getState().getGameResult().payout
    const skinName = translatePotSkine(type)
    const spine = spineManager.create('symbol.skel', 'symbol.atlas', 'giftSymbol', SpineGroupType.GiftedSymbol, skinName)
    node.addChild(spine)
    spine.position.set(0, 0)
    spine.state.setAnimation(0, 'tips2', false)
    const listener = {
      complete: (entry: TrackEntry) => {
        if (entry.animation?.name !== 'tips2') return

        spine.state.removeListener(listener)

        if (spinPayout) {
          spine.state.setAnimation(0, 'mSymbolLoop', true)
        }
      },
    }

    spine.state.addListener(listener)
  }

  public async _playGiftSymbol(giftList: GiftedSymbol[]): Promise<void> {
    const isFreeGame = useGameStore.getState().isFreeGame
    const tasks = giftList.map((gift, index) => {
      return new Promise<void>((resolve) => {
        const { x, y } = gift.position
        const symbol = gift.newSymbol
        const upperNode = tableService.getUpperNode(x, y)
        const potItem = symbol.type === SymbolType.Wild ? tableService.potList[1] : tableService.potList[2]

        /** ⭐ symbol node */
        const symbolNode = createSymbolContainer(symbol)
        symbolNode.pivot.set(gameConfig.nodeWidth / 2, gameConfig.nodeHeight / 2 - 0.5)
        /** ⭐ spine */
        const skinName = symbol.type === SymbolType.Wild ? 'Wild' : 'Multiplier1'
        const spine = spineManager.create('symbol.skel', 'symbol.atlas', 'giftSymbol', SpineGroupType.GiftedSymbol, skinName)
        spine.position.set(0, 0)
        upperNode.addChild(spine)

        const animationName = isFreeGame ? 'thunder2' : 'thunder'
        spine.state.setAnimation(0, animationName, false)

        /** ⭐ FreeGame bind pot */
        if (isFreeGame) {
          const bone = spine.skeleton.findBone('bind_multiplier2') as Bone
          if (potItem?.spine && bone) {
            const potPosition = potItem.spine.getGlobalPosition()
            const localPotPosition = spine.toLocal(potPosition)
            bone.x = localPotPosition.x
            bone.y = -(localPotPosition.y - 100)
          }
        }

        /** ⭐ 延遲顯示 symbol + gasp */
        const delay = isFreeGame ? 0.85 : 0.15
        gsap.delayedCall(delay, () => {
          if (index === 0) soundManager.play(GameSoundType.ThreeComboOlympus_SymbolTip)
          const symbolSize = this.getMultiplierScale(gift.newSymbol.multiplier)
          symbolNode.scale.set(symbolSize)
          upperNode.addChild(symbolNode)

          gsap.to(symbolNode.scale, { x: 1, y: 1, duration: 0.4, ease: 'power2.out', onComplete: resolve })
        })
      })
    })

    if (tasks.length) {
      soundManager.play(GameSoundType.ThreeComboOlympus_SymbolThunder)
      await Promise.all(tasks)
    }
  }

  public applyGiftSymbolsToReels(giftList: GiftedSymbol[]) {
    giftList.forEach((gift) => {
      const { x, y } = gift.position
      const reel = this.reelNodes?.[x]
      if (!reel) return
      const childIndex = reel.children.length - 1 - y
      if (childIndex < 0 || childIndex >= reel.children.length) return
      const symbolContainer = reel.children[childIndex] as Container | undefined
      if (!symbolContainer) return
      changeNode(symbolContainer, gift.newSymbol)
    })
  }

  public async tableExpand() {
    const animationPromises: Promise<void>[] = []
    const slot = tableService.getSlotConatiner()
    tableService.upperContainer2.y -= gameConfig.nodeHeight
    for (let i = 0; i < this.reelNodes.length; i++) {
      const reel = this.reelNodes[i]
      reel.children.forEach((symbol) => {
        const nodeName = symbol.name
        const upperNode2 = tableService.upperContainer2.getChildByName(`${nodeName}`) as Container
        symbol.y += 116
        if (upperNode2) upperNode2.y += 116
      })
    }

    animationPromises.push(
      new Promise<void>((resolve) => {
        slot.y = 554
        // 第一個 child 是背景 Graphics
        const height = gameConfig.expandReelHeight
        const firstChild = slot.children[0] as Container
        firstChild.height = height
        // 最後一個 child 是 mask Graphics
        const lastChild = slot.children[slot.children.length - 1] as Container
        lastChild.height = height
        resolve()
      }),
    )
    await Promise.all(animationPromises)
  }

  public async removeTableExpand() {
    const animationPromises: Promise<void>[] = []
    const removeList: Container[] = []

    const slot = tableService.getSlotConatiner()
    const DURATION = 0.2
    const OFFSET_Y = 116

    // ---- Slot 高度與位置動畫 ----
    const slotPromise = new Promise<void>((resolve) => {
      gsap.to(slot, {
        y: slot.y + OFFSET_Y,
        duration: DURATION,
        ease: 'linear',
      })
      gsap.to([slot.children[0], slot.children[slot.children.length - 1]], {
        height: gameConfig.expandReelHeight - OFFSET_Y,
        duration: DURATION,
        ease: 'linear',
        onComplete: resolve,
      })
    })
    animationPromises.push(slotPromise)

    for (const reel of this.reelNodes) {
      const symbols = reel.children as Container[]

      removeList.push(symbols[0])

      const movePromise = new Promise<void>((resolve) => {
        gsap.to(symbols, {
          y: `-=${OFFSET_Y}`,
          duration: DURATION,
          ease: 'linear',
          onComplete: resolve,
        })
      })
      animationPromises.push(movePromise)
    }
    soundManager.play(GameSoundType.ThreeComboOlympus_FrameRegain)
    await Promise.all(animationPromises)

    for (const symbol of removeList) {
      const reel = symbol.parent as Container
      if (reel && reel.children.includes(symbol)) {
        reel.removeChild(symbol)
        symbol.destroy()
      }
    }
  }

  public immediateStop() {
    if (this.shouldImmediatelyStop) return
    this.shouldImmediatelyStop = true
    ga4Info.recordImmediateStop()
  }

  public getMultiplierScale(multiplier: number): number {
    if (multiplier <= 8) return 1.2
    if (multiplier <= 18) return 1.6
    if (multiplier <= 100) return 2.2
    return 2.6
  }
}
